/**
 * Idempotent WASM initialization helper.
 * Safe to call from both main thread and workers.
 */

import { dlog } from "../debug/debugLogger.ts";
import init, { type InitOutput, initSync } from "./pkg/poseref_wasm.js";

let _initPromise: Promise<InitOutput> | null = null;
let _ready = false;
let _memory: WebAssembly.Memory | null = null;

/** Returns true once the WASM module has finished initializing. */
export function isWasmReady(): boolean {
  return _ready;
}

/**
 * Current size of the main-thread WASM linear memory in bytes, or 0 if the
 * module is not yet initialized. Workers each own their own linear memory and
 * are NOT reflected here — the memory governor accepts that gap for v1.
 */
export function getWasmMainHeapBytes(): number {
  return _memory ? _memory.buffer.byteLength : 0;
}

export function ensureWasmReady(): Promise<InitOutput> {
  if (!_initPromise) {
    const t0 = performance.now();
    dlog.info("wasm", "wasm.init.start", "initializing WASM module");
    _initPromise = init().then((out: InitOutput) => {
      _ready = true;
      _memory = out.memory;
      dlog.info(
        "wasm",
        "wasm.init.done",
        `WASM ready (${(performance.now() - t0).toFixed(0)}ms)`,
        { durationMs: +(performance.now() - t0).toFixed(0) },
      );
      return out;
    });
  }
  return _initPromise!;
}

/**
 * Synchronous WASM init from raw bytes (for test environments where
 * fetch() is unavailable).
 */
export function ensureWasmReadyFromBytes(bytes: BufferSource): InitOutput {
  if (_ready) return _initPromise as unknown as InitOutput;
  const out = initSync({ module: bytes });
  _ready = true;
  _memory = out.memory;
  _initPromise = Promise.resolve(out);
  return out;
}

/**
 * Main-thread variant that surfaces WASM init failures/stalls via toast.
 * Workers should continue using plain `ensureWasmReady()` (no DOM access).
 */
export function ensureWasmReadyWithNotification(
  timeoutMs = 10_000,
): Promise<InitOutput> {
  let timerId: ReturnType<typeof setTimeout> | null = null;

  const timeout = new Promise<never>((_, reject) => {
    timerId = setTimeout(() => reject(new Error("timed out")), timeoutMs);
  });

  return Promise.race([
    ensureWasmReady().finally(() => {
      if (timerId != null) clearTimeout(timerId);
    }),
    timeout,
  ]).catch((err) => {
    // Lazy-import keeps this file safe for worker contexts
    import("../ui/toastBus.ts")
      .then(({ showToast }) => {
        showToast(
          `WASM initialization failed (${err instanceof Error ? err.message : "unknown error"}). Some features may be unavailable.`,
          { severity: "error", duration: 10_000 },
        );
      })
      .catch(() => {});
    throw err;
  });
}
